import { supabase } from "../../../supabase";
import isModuleRelevant from "./isModuleRelevant";

/**
 * Fetches all active training modules for a given user ID,
 * optionally including only those attempts the user has passed.
 *
 * @param {string} userId - The ID of the user whose modules are to be fetched.
 * @param {boolean} [passedAttempts=false] - If true, only fetch attempts the user has passed.
 * @returns {Promise<{data: any[], error: any}>} - A promise resolving to an object
 * containing the fetched modules and any errors.
 */
export async function fetchAllModules(userId, passedAttempts = false) {
  let query = supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("training_master")
    .select(
      "*, training_user_attempt_index (*), training_supplier_index(suppliers_master_id), training_refs_product_type_index(refs_product_type_id)"
    )
    .eq("active", true)
    .eq("is_deleted", false)
    .eq("training_user_attempt_index.users_master_id", userId)
    .eq("training_supplier_index.is_active", true)
    .eq("training_refs_product_type_index.is_active", true)
    .order("module_name");

  if (passedAttempts) {
    query = query.eq("training_user_attempt_index.pass", true);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Filters training modules based on their relevance to a given user.
 *
 * @param {object} user - The user object with relevant info.
 * @param {Array} userSuppliers - An array containing suppliers assigned to the user
 * @param {Array} userProductTypes - An array containing product types assigned to the user
 * @param {Array} modules - The array of modules to filter, or an empty array by default.
 * @returns {Array} - An array of modules that are relevant to the user.
 */
export function filterRelevantModules(
  user,
  userSuppliers = [],
  userProductTypes = [],
  modules = []
) {
  const moduleArr = [];

  if (user && modules.length > 0) {
    modules.forEach(module => {
      const { training_refs_product_type_index, training_supplier_index } =
        module;
      const productTypeMap = training_refs_product_type_index.map(
        productType => productType.refs_product_type_id
      );
      const supplierMap = training_supplier_index.map(
        supplier => supplier.suppliers_master_id
      );

      const moduleResponse = isModuleRelevant(
        productTypeMap,
        supplierMap,
        module.training_types,
        module.is_compulsory,
        module.refs_structure_level_id,
        userProductTypes,
        userSuppliers,
        user.is_leader,
        user.refs_max_structure_level_id,
        user.id
      );

      if (moduleResponse.relevant || user.is_admin) {
        moduleArr.push({
          ...module,
          status: {
            compulsory: moduleResponse.compulsory,
            passed: module.training_user_attempt_index.some(
              attempt => attempt.pass === true
            ), // Check if any attempt has passed
          },
        });
      }
    });
  }
  return moduleArr;
}

/**
 * Checks the user's status.
 * @param {object} user - The user object with relevant info.
 * @param {Array} suppliers - An array containing suppliers assigned to the user
 * @param {Array} productTypes - An array containing product types assigned to the user
 * @param {Array} modules - The array of modules to filter, or an empty array by default.
 * @param {object} error - An error object in case of error.
 * @returns {object} - An object with the following properties:
 * - `relevantModules`: The number of modules relevant to the user.
 * - `passedRelevantModules`: The number of relevant modules passed by the user.
 * - `compulsoryModules`: The number of compulsory modules.
 * - `passedCompulsoryModules`: The number of compulsory modules passed by the user.
 * - `status`: An integer value representing the user's status:
 *   - `-1`: Error retrieving data.
 *   - `0`: All modules passed.
 *   - `101`: Compulsory modules passed.
 *   - `102`: Minimum criteria not met.
 */
export function getUniversityStatus(
  user,
  suppliers = [],
  productTypes = [],
  modules = [],
  error = undefined
) {
  let status = 102;

  if (error) {
    console.error(error);
    return {
      relevantModules: 0,
      passedRelevantModules: 0,
      compulsoryModules: 0,
      passedCompulsoryModules: 0,
      status: -1,
    };
  }

  const moduleArr = filterRelevantModules(
    user,
    suppliers,
    productTypes,
    modules
  );

  const moduleArrStatus = moduleArr.map(module => module.status);

  const relevantModules = moduleArrStatus.length; // count all relevant modules

  const passedRelevantModules = moduleArrStatus.reduce((acc, item) => {
    return item.passed ? acc + 1 : acc;
  }, 0); // count all passed relevant modules

  const compulsoryModules = moduleArrStatus.reduce((acc, item) => {
    return item.compulsory ? acc + 1 : acc;
  }, 0); // count all relevant and compulsory modules

  const passedCompulsoryModules = moduleArrStatus.reduce((acc, item) => {
    return item.compulsory && item.passed ? acc + 1 : acc;
  }, 0); // count all passed relevant and compulsory modules

  if (passedRelevantModules >= relevantModules) {
    status = 0;
  } else if (passedCompulsoryModules >= compulsoryModules) {
    status = 101;
  }

  const universityStatus = {
    relevantModules,
    passedRelevantModules,
    compulsoryModules,
    passedCompulsoryModules,
    status,
  };

  return universityStatus;
}

/**
 * Fetches the product types and active suppliers of another user.
 * @param {number} id - The ID of the user whose criteria is to be fetched.
 * @returns {Promise<{data: {productTypes: Array<object>, activeSuppliers: Array<object>}, error: object}>}
 * A promise resolving to an object containing the fetched criteria and any errors.
 */
export async function fetchOtherUserCriteria(id) {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("users_master")
    .select(
      "users_supplier_index(suppliers_master(*)), users_product_type_index(refs_product_type(*))"
    )
    .eq("id", id)
    .eq("users_supplier_index.suppliers_master.is_deleted", false)
    .lte("users_supplier_index.suppliers_master.supplier_status", 2)
    .eq("users_product_type_index.refs_product_type.is_deleted", false);

  if (error) {
    console.error(error.message);
    return {
      error,
      data: {
        productTypes: [],
        activeSuppliers: [],
      },
    };
  }

  const userCriteriaData = data[0];

  const { users_product_type_index, users_supplier_index } = userCriteriaData;

  const userSupplierMap = users_supplier_index
    .filter(({ suppliers_master }) => suppliers_master !== null)
    .map(({ suppliers_master }) => suppliers_master);

  const userProductTypeMap = users_product_type_index
    .filter(({ refs_product_type }) => refs_product_type !== null)
    .map(({ refs_product_type }) => refs_product_type);

  const response = {
    error,
    data: {
      productTypes: userProductTypeMap,
      activeSuppliers: userSupplierMap,
    },
  };

  return response;
}
